import {
    invoiceStatusEnum,
    invoiceTypeMap,
    PARTIAL_PTS_VISIBILITY_CONTEXT,
    normalizePartialPtsVisibilityFlags,
} from '../../../Configurations/invoicingConfig';

/** Convert any API-provided value (strings, arrays, objects/dictionaries) into a trimmed string. */
const toStringSafe = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (Array.isArray(v)) return v.map(toStringSafe).filter(Boolean).join(' ');
    if (typeof v === 'object') return Object.values(v).map(toStringSafe).filter(Boolean).join(' ');
    return String(v).trim();
};

/** Canonicalize severity levels. */
const normalizeLevel = (lvl) => {
    const s = String(lvl || '').toLowerCase();
    if (s === 'error') return 'error';
    if (s === 'warning' || s === 'warn') return 'warning';
    return s || 'info';
};

/** Normalize and de-duplicate issues by (level|message) without using iterator syntax. */
const dedupeNormalizedIssues = (arr) => {
    const seen = Object.create(null);
    const list = Array.isArray(arr) ? arr : [];

    return list.reduce((out, it) => {
        const level = normalizeLevel((it && (it.severity ?? it.level)) || '');
        const message = toStringSafe(it && (it.message ?? it.issueCode ?? ''));
        const value = toStringSafe(it && it.value);

        const displayMessage = message || value;
        if (!displayMessage) return out;

        const key = `${level}|${displayMessage}`;
        if (seen[key]) return out;

        seen[key] = true;
        out.push({ level, message: displayMessage });
        return out;
    }, []);
};

export default function adaptApiInvoiceToUi(api, opts = {}) {
    const { forcePreInvoicedLabel = false } = opts;
    if (!api) return null;

    // --- pull from API with safe defaults ---
    const {
        id: apiId,
        fileName,
        customerId,
        clientName,
        invoiceNumber,
        clinicianName,
        numberOfLocations,
        bookingIds = [],
        invoiceDate,
        accountingDate,
        status,
        invoiceType,
        totalInvoiceAmount,
        netInvoiceAmount,
        totalTax,
        totalDiscount,
        amountDue,
        isPreInvoice,
        isCorrection,
        hasErrors,
        hasWarnings,
        invoiceIssues = [],
        timesheets = [],
        expenses = [], // grouped
        selectable,
        isHighPriority,
        collectorName,
        collectorSfId,
        isLatest,
    } = api || {};

    const bookingId = apiId; // treat invoice.id as bookingId for selection
    const rawStatus = typeof status === 'string' ? status.trim() : '';
    const displayStatus =
        forcePreInvoicedLabel && rawStatus === invoiceStatusEnum.Invoiced
            ? invoiceStatusEnum.PreInvoiced
            : rawStatus;
    // ---------------- Timesheets (parent with sub-items: lineItems) ----------------
    const uiTimesheets = (Array.isArray(timesheets) ? timesheets : []).map((ts) => {
        let { groupedTimesheets } = ts;
        if (ts.splitByWorklocation === false) {
            // if the timesheet is not split by work location, we need to aggregate the timesheets line items into a single unnamed location
            groupedTimesheets = [
                {
                    locationId: null,
                    locationName: '',
                    lineItems:
                        groupedTimesheets?.reduce((acc, g) => acc.concat(g.lineItems || []), []) ||
                        [],
                },
            ];
        }

        return {
            // fields exactly as in the API payload
            id: ts?.processedTimesheetId,
            weekEnding: ts?.weekEnding,
            numberOfLocations: ts?.numberOfLocations,
            bookingId: ts?.bookingId,
            timesheetId: ts?.timesheetId,
            processedTimesheetId: ts?.processedTimesheetId,
            aggregateProcessedTimesheetId: ts?.aggregateProcessedTimesheetId,
            clinicianId: ts?.clinicianId,
            totals: ts?.totals,
            locationsCount: ts?.numberOfLocations,
            isHighPriority: ts?.isHighPriority,
            // Partial PTS visibility flags (AE2-6308); missing → false
            ...normalizePartialPtsVisibilityFlags(PARTIAL_PTS_VISIBILITY_CONTEXT.Management, ts),
            groupedTimesheets,
        };
    });

    // ---------------- Expenses (grouped shape ONLY after revert) ----------------
    const expensesGroups = Array.isArray(expenses)
        ? expenses.map((group) => {
              const items = Array.isArray(group?.items) ? group.items : [];
              const groupIsCorrection =
                  !!group?.isCorrection || items.some((it) => !!it?.isCorrection);
              const groupIsHighPriority =
                  !!group?.isHighPriority || items.some((it) => !!it?.isHighPriority);

              return {
                  id: group?.bookingId ?? bookingId,
                  bookingId: group?.bookingId ?? bookingId,
                  isCorrection: groupIsCorrection,
                  isHighPriority: groupIsHighPriority,
                  totals: {
                      totalToBill: Number(group?.totalToBill ?? 0),
                      totalToPay: Number(group?.totalToPay ?? 0),
                  },
                  items: items.map((it) => ({
                      id: it?.id,
                      transactionDate: it?.transactionDate ?? null,
                      startDate: it?.startDate ?? null,
                      endDate: it?.endDate ?? null,
                      expenseType: it?.expenseType ?? it?.type ?? '',
                      billAmount: Number(it?.billAmount ?? 0),
                      amount: Number(it?.billAmount ?? 0),
                      isCorrection: !!it?.isCorrection,
                  })),
              };
          })
        : [];

    // ---------------- Header fields and totals ----------------
    const amount = Number(netInvoiceAmount ?? totalInvoiceAmount ?? 0);

    // ---------------- Normalize & DEDUPE invoice issues ----------------
    const issues = dedupeNormalizedIssues(invoiceIssues);
    const issuesCount = issues.length;
    const hasAnyErrorIssue = issues.some((i) => i.level === 'error');
    const hasAnyWarningIssue = issues.some((i) => i.level === 'warning');

    // ---------------- UI helpers moved from InvoiceItemContainer ----------------

    // Booking numbers as badges: [{ id, text: "BKG-123" }]
    const getBookingNumberBadges = () => {
        const ids = bookingIds;
        if (ids == null) return [{ id: 'unknown', text: 'Unknown' }];
        const list = Array.isArray(ids) ? ids : [ids];
        return [...new Set(list)].map((id) => ({ id: String(id), text: `BKG-${id}` }));
    };

    // Combined items list for the collapse (timesheet/expense)
    const getItems = () => {
        const key = String(invoiceType || '').toLowerCase();
        const mapTsItems = () =>
            uiTimesheets.map((ts) => ({ type: 'timesheet', id: ts.id, data: ts }));
        const mapExpItems = () =>
            expensesGroups
                .filter((exp) => (exp?.items?.length ?? 0) > 0)
                .map((exp) => ({ type: 'expense', id: exp.id, data: exp }));

        switch (key) {
            case 'timeonly':
                return mapTsItems();
            case 'expenseonly':
                return mapExpItems();
            case 'timeandexpense':
            default:
                return [...mapTsItems(), ...mapExpItems()];
        }
    };

    return {
        // selection/context ids
        bookingId,
        clientId: Number(customerId ?? 0) || null,

        // list header fields
        clientName,
        customerId,
        invoiceNumber,
        clinicianName,
        bookingIds,
        invoiceDate,
        accountingDate,
        // keep API value for logic
        statusCode: rawStatus,
        // show this in the UI pill/column
        status: displayStatus,
        invoiceType,
        amount,
        totalTax: Number(totalTax ?? 0),
        totalDiscount: Number(totalDiscount ?? 0),
        amountDue: amountDue != null ? Number(amountDue) : null,
        isPreInvoice: !!isPreInvoice,
        isCorrection: !!isCorrection,
        isHighPriority: !!isHighPriority,

        // flags + normalized issues
        hasErrors: !!hasErrors,
        hasWarnings: !!hasWarnings,
        issues, // normalized & de-duplicated
        issuesCount, // unique count

        selectable: selectable ?? true,
        numberOfLocations,
        collectorName,
        collectorSfId,

        // moved-from-row convenience
        displayType: invoiceTypeMap[invoiceType] || '',
        bookingNumberBadges: getBookingNumberBadges(),
        items: getItems() || [],

        // optional derived flags
        _derived: {
            hasAnyErrorIssue,
            hasAnyWarningIssue,
        },

        // for pdf
        fileName,
        isLatest,
    };
}
